import React, { useRef, useCallback } from 'react'
import { Button, Modal, Typography } from 'antd'
import { useDrag, useDrop } from 'react-dnd'
import PrivateImage from 'src/components/PrivateImage'
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from '@ant-design/icons'
import s from './index.module.scss'

const { Text } = Typography

interface CarouselItemProps {
  layoutCard: LayoutCard
  categoryId: string
  onDrop: (drapId: string, dropId: string) => void
  onRemove: (layoutCard: LayoutCard) => void
  onEdit?: (item: any) => void
  thumbnailSize?: 'SMALL' | 'MEDIUM' | 'LARGE'
}

const CarouselItem: React.FC<CarouselItemProps> = React.memo(
  ({
    layoutCard,
    categoryId,
    thumbnailSize,

    onDrop = () => null,
    onRemove = () => null,
    onEdit,
  }) => {
    const ref = useRef<HTMLDivElement>(null)
    const dndType = `card_${categoryId}`

    const [, drag] = useDrag(
      () => ({
        type: dndType,
        item: {
          id: layoutCard.id,
        },
      }),
      [layoutCard.id, dndType]
    )
    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: dndType,
        drop(item: any) {
          if (onDrop && item.id !== layoutCard.id) {
            onDrop(item.id, layoutCard.id)
          }
        },
        collect: (monitor) => {
          return {
            isOver: monitor.isOver({ shallow: true }),
          }
        },
      }),
      [onDrop, layoutCard.id, dndType]
    )

    const onRemoveClick = useCallback(() => {
      Modal.confirm({
        title: (
          <p>
            You&apos;re deleting <strong>{layoutCard.title}</strong>
          </p>
        ),
        icon: <ExclamationCircleOutlined />,
        onOk: () => onRemove && onRemove(layoutCard),
      })
    }, [layoutCard, onRemove])

    drop(drag(ref))

    return (
      <div className={s.carouselItem}>
        <div
          ref={ref}
          className={s.carouselItemCard}
          style={{
            border: isOver ? '2px dashed #1890ff' : 'none',
            opacity: isOver ? 0.8 : 1,
          }}
        >
          {thumbnailSize === 'LARGE' && (
            <Button
              type="primary"
              className="btn-primary"
              style={{ borderRadius: 0 }}
              shape="circle"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit?.(layoutCard)}
            />
          )}
          <PrivateImage
            src={layoutCard.cover_url}
            width={'100%'}
            height={140}
            preview={false}
          />
          <div className={s.carouselItemMeta}>
            <Text strong>{layoutCard.title}</Text>
            <Text italic type="secondary">
              {layoutCard.title}
            </Text>
          </div>

          <Button
            type="primary"
            danger
            shape="circle"
            icon={<DeleteOutlined />}
            size="small"
            className={s.carouselItemDelBtn}
            onClick={onRemoveClick}
          />
        </div>
      </div>
    )
  }
)

CarouselItem.displayName = 'CarouselItem'
export default CarouselItem
